'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'

// The notice (the design system's .notice): one line at the bottom centre
// of the content area, on the card surface with a hairline and the notice
// shadow, an icon in the primary colour, a Dismiss ×. For confirmations and
// transient failures ("Schedule saved", "Could not queue the job"); a
// persistent problem stays inline on the page. useNotice() posts one; the
// provider keeps a single current notice and clears it after a few seconds.

export type NoticeTone = 'info' | 'success' | 'warning'
type NoticeItem = { id: number; text: ReactNode; tone: NoticeTone; ttl: number }

const NoticeContext = createContext<{ notify: (text: ReactNode, opts?: { tone?: NoticeTone; ttl?: number }) => void }>({ notify: () => {} })

export function useNotice() {
  return useContext(NoticeContext)
}

export function NoticeProvider({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<NoticeItem | null>(null)
  const seq = useRef(0)
  const notify = useCallback((text: ReactNode, opts?: { tone?: NoticeTone; ttl?: number }) => {
    seq.current += 1
    setItem({ id: seq.current, text, tone: opts?.tone ?? 'info', ttl: opts?.ttl ?? 5000 })
  }, [])
  useEffect(() => {
    if (!item) return
    const t = setTimeout(() => setItem((cur) => (cur?.id === item.id ? null : cur)), item.ttl)
    return () => clearTimeout(t)
  }, [item])
  const value = useMemo(() => ({ notify }), [notify])
  const Icon = item?.tone === 'success' ? CheckCircle2 : item?.tone === 'warning' ? AlertTriangle : Info
  return (
    <NoticeContext.Provider value={value}>
      {children}
      {item && (
        <div role="status" className="fixed bottom-6 left-1/2 z-[60] flex max-w-[calc(100vw-30px)] -translate-x-1/2 items-center justify-between gap-4 border border-border bg-card py-1.5 pl-4 pr-2 text-nav text-foreground shadow-notice lg:left-[calc(50%+116px)]">
          <span className="flex items-center gap-2">
            <Icon className={`h-4 w-4 shrink-0 ${item.tone === 'warning' ? 'text-warning' : item.tone === 'success' ? 'text-success' : 'text-primary'}`} />
            {item.text}
          </span>
          <button type="button" onClick={() => setItem(null)} aria-label="Dismiss" className="flex h-7 w-7 items-center justify-center rounded-[2px] text-muted-foreground hover:bg-secondary hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </NoticeContext.Provider>
  )
}

/** A persistent inline band (the design system's .attention-note). */
export function AttentionNote({ children, tone = 'warning', className = '' }: { children: ReactNode; tone?: 'warning' | 'danger' | 'info'; className?: string }) {
  const cls = tone === 'danger' ? 'bg-danger-surface text-destructive' : tone === 'info' ? 'bg-signal-surface text-signal' : 'bg-warning-surface text-warning'
  return <div className={`rounded-[4px] px-3 py-3 text-small leading-relaxed ${cls} ${className}`}>{children}</div>
}
